<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { toast } from "vue-sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePwaUpdate } from "@/composables/use-pwa-update";

let offlineToastId: number | string | null = null;
let hasShownOfflineToast = false;

const { needRefresh, offlineReady, updateNow, dismissOffline, dismissUpdate } = usePwaUpdate();
const isUpdating = ref(false);

const isUpdateDialogOpen = computed({
  get: () => needRefresh.value,
  set: (open) => {
    if (!open) dismissUpdate();
  },
});

const closeOfflineToast = () => {
  if (offlineToastId !== null) {
    toast.dismiss(offlineToastId);
  }
  offlineToastId = null;
  dismissOffline();
};

const handleUpdateNow = async () => {
  if (isUpdating.value) return;

  isUpdating.value = true;
  try {
    await updateNow();
  } catch (error) {
    isUpdating.value = false;
    console.error("Unable to apply service worker update:", error);
  }
};

watch(
  offlineReady,
  (ready) => {
    if (!ready || hasShownOfflineToast) return;

    hasShownOfflineToast = true;
    const nextToastId = toast("Condor está lista para usarse sin conexión.", {
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: "OK",
        onClick: closeOfflineToast,
      },
      onDismiss() {
        offlineToastId = null;
        dismissOffline();
      },
    });

    offlineToastId = typeof nextToastId === "number" || typeof nextToastId === "string"
      ? nextToastId
      : null;
  },
  { immediate: true },
);
</script>

<template>
  <Dialog v-model:open="isUpdateDialogOpen">
    <DialogContent :show-close-button="false" class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Nueva versión disponible</DialogTitle>
        <DialogDescription>
          Hay una actualización de Condor lista para instalar.
        </DialogDescription>
      </DialogHeader>

      <DialogFooter class="gap-2 sm:justify-end">
        <Button type="button" variant="outline" :disabled="isUpdating" @click="dismissUpdate">
          Ahora no
        </Button>
        <Button type="button" :disabled="isUpdating" @click="handleUpdateNow">
          {{ isUpdating ? "Actualizando..." : "Actualizar ahora" }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
